import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  Grid,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Link,
  Alert,
  Skeleton,
  ButtonBase,
  Button,
  Chip,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from '@mui/material';
import {
  Payments as PayrollIcon,
  Send as SendIcon,
  WarningAmber as AlertIcon,
  AccessTime as ClockIcon,
  ArrowForward as ArrowIcon,
  CheckCircleOutline as AllClearIcon,
  Approval as ApprovalIcon,
  BeachAccess as TimeOffIcon,
  Receipt as ExpenseIcon,
  PlayArrow as PlayIcon,
  Person as PersonIcon,
  InfoOutlined as InfoIcon,
} from '@mui/icons-material';
import { useAuth } from '../context/AuthContext';
import { canListInvoices, canReadAdminInbox } from '../utils/endpointAccess';
import { usePipeline } from '../context/PipelineContext';
import { useFeatures } from '../context/FeaturesContext';
import {
  getAdminInbox,
  getInvoices,
  getTimeEntries,
  getPendingApprovalCount,
  getPendingPTOCount,
  getActiveClockIns,
  cancelClockIn,
} from '../api';
import { formatTimeInTimezone, getUserTimezone } from '../utils/formatTime';
import { formatCurrency, formatPay } from '../utils/formatUtils';
import MetricTile from './MetricTile';
import StatusChip from './StatusChip';
import ClockCard from './ClockCard';

// Action-inbox home (docs/ui-refresh-mockup.html): "what needs you"
// worklist + this-period metric strip + recent invoices + recent activity.
// Permission-composed — admins get tenant-wide sections, managers get only
// what their permissions allow, with metrics scoped to their team by the
// server. Employees keep the clock-first Dashboard; the switch lives there.

const fmtDate = (d) => {
  if (!d) return '';
  const [y, m, day] = String(d).split('T')[0].split('-').map(Number);
  return new Date(Date.UTC(y, m - 1, day)).toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' });
};

// Current + previous pay-period windows. Same convention as the employee
// Dashboard: period type comes from the local dashboardSettings preference.
const getPayPeriodWindows = () => {
  let payPeriod = 'weekly';
  try {
    payPeriod = JSON.parse(localStorage.getItem('dashboardSettings') || '{}').payPeriod || 'weekly';
  } catch { /* default */ }

  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const day = 24 * 60 * 60 * 1000;

  let start, end, prevStart, prevEnd;
  if (payPeriod === 'biweekly') {
    const startOfYear = new Date(today.getFullYear(), 0, 1);
    const periodNum = Math.floor((today - startOfYear) / day / 14);
    start = new Date(startOfYear.getTime() + periodNum * 14 * day);
    end = new Date(start.getTime() + 13 * day);
    prevStart = new Date(start.getTime() - 14 * day);
    prevEnd = new Date(start.getTime() - 1 * day);
  } else if (payPeriod === 'semimonthly') {
    if (today.getDate() <= 15) {
      start = new Date(today.getFullYear(), today.getMonth(), 1);
      end = new Date(today.getFullYear(), today.getMonth(), 15);
      prevStart = new Date(today.getFullYear(), today.getMonth() - 1, 16);
      prevEnd = new Date(today.getFullYear(), today.getMonth(), 0);
    } else {
      start = new Date(today.getFullYear(), today.getMonth(), 16);
      end = new Date(today.getFullYear(), today.getMonth() + 1, 0);
      prevStart = new Date(today.getFullYear(), today.getMonth(), 1);
      prevEnd = new Date(today.getFullYear(), today.getMonth(), 15);
    }
  } else {
    start = new Date(today.getTime() - today.getDay() * day);
    end = new Date(start.getTime() + 6 * day);
    prevStart = new Date(start.getTime() - 7 * day);
    prevEnd = new Date(start.getTime() - 1 * day);
  }

  const iso = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  const dayInPeriod = Math.floor((today - start) / day) + 1;
  const daysInPeriod = Math.floor((end - start) / day) + 1;
  return { start, end, dayInPeriod, daysInPeriod, params: {
    start_date: iso(start), end_date: iso(end), prev_start_date: iso(prevStart), prev_end_date: iso(prevEnd),
  } };
};

const navigateToTab = (tab) => window.dispatchEvent(new CustomEvent('hourtrax-navigate-tab', { detail: { tab } }));

// Task card: left semantic stripe, tinted icon, kicker + big count +
// one-line detail + inline action (mockup "action-inbox task card").
function TaskCard({ tone, icon, kicker, count, countUnit, detail, actionLabel, onAction }) {
  const toneColor = { good: 'success', warn: 'warning', crit: 'error', info: 'info' }[tone] || 'info';
  return (
    <Card sx={{ position: 'relative', overflow: 'hidden', p: 2, pb: 1.75, display: 'flex', flexDirection: 'column', gap: 1.25, height: '100%' }}>
      <Box sx={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: 3, bgcolor: `${toneColor}.main` }} />
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
        <Box sx={{ width: 30, height: 30, borderRadius: '8px', display: 'grid', placeItems: 'center', flex: 'none', bgcolor: `${toneColor}.soft`, color: `${toneColor}.main`, '& svg': { fontSize: 17 } }}>
          {icon}
        </Box>
        <Typography variant="overline" sx={{ color: 'text.faint', lineHeight: 1.4, letterSpacing: '0.06em' }}>{kicker}</Typography>
      </Box>
      <Typography component="div" sx={{ fontSize: '1.55rem', fontWeight: 700, letterSpacing: '-0.02em', lineHeight: 1 }}>
        {count}
        {countUnit && <Typography component="span" sx={{ fontSize: '0.8125rem', fontWeight: 500, color: 'text.secondary', ml: 0.75 }}>{countUnit}</Typography>}
      </Typography>
      <Typography variant="body2" sx={{ color: 'text.secondary', lineHeight: 1.45, flex: 1 }}>{detail}</Typography>
      <ButtonBase
        onClick={onAction}
        sx={{ alignSelf: 'flex-start', gap: 0.75, fontFamily: 'inherit', fontSize: '0.8125rem', fontWeight: 600, color: `${toneColor}.main`, '&:hover svg': { transform: 'translateX(2px)' }, '& svg': { fontSize: 14, transition: 'transform .12s' } }}
      >
        {actionLabel}
        <ArrowIcon />
      </ButtonBase>
    </Card>
  );
}

// "Currently Clocked In" (moved here from the employee Dashboard: managers and
// admins never see that screen). Lists open shifts the caller may see and lets a
// manager/admin remove a stale one (e.g. after a refused offline Clock Out).
// DELETE /api/clockin/:employee_id is scoped by canAccessEmployee on the server,
// so a manager can only cancel a direct report's clock-in. GET /active answers
// 403 to anyone who is not a manager / approver: the section then stays hidden.
function CurrentlyClockedIn() {
  const [rows, setRows] = useState([]);
  const [cancelTarget, setCancelTarget] = useState(null);
  const [cancelBusy, setCancelBusy] = useState(false);
  const [cancelError, setCancelError] = useState('');

  const load = React.useCallback(async () => {
    try {
      const res = await getActiveClockIns();
      setRows(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      if (err.response?.status !== 403) console.error('Error loading active clock-ins:', err);
      setRows([]); // 403 (not a manager/approver) or any failure: hide the section
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const confirmCancel = async () => {
    if (!cancelTarget) return;
    setCancelBusy(true);
    try {
      await cancelClockIn(cancelTarget.employee_id, cancelTarget.id);
      setCancelError('');
      setCancelTarget(null);
      load();
    } catch (err) {
      setCancelError(err.response?.data?.error || 'Failed to cancel the clock-in');
      setCancelTarget(null);
      if (err.response?.status === 409) load(); // the shift changed under us: refresh the list
    } finally {
      setCancelBusy(false);
    }
  };

  if (rows.length === 0 && !cancelError) return null;
  return (
    <Box>
      <Typography variant="overline" sx={{ color: 'text.faint', letterSpacing: '0.08em', display: 'flex', alignItems: 'center' }}>
        <PlayIcon sx={{ mr: 0.5, fontSize: 16, color: 'success.main' }} />
        Currently Clocked In ({rows.length})
      </Typography>
      {cancelError && <Alert severity="error" sx={{ mt: 1 }} onClose={() => setCancelError('')}>{cancelError}</Alert>}
      <Grid container spacing={1.75} sx={{ mt: -0.5 }}>
        {rows.map((clockIn) => {
          const t = clockIn.clock_in_time ? new Date(clockIn.clock_in_time).getTime() : null;
          const elapsed = t && !isNaN(t) ? Math.max(0, Math.floor((Date.now() - t) / 1000)) : 0;
          return (
            <Grid item xs={12} sm={6} md={4} key={clockIn.id}>
              <Card variant="outlined" sx={{ p: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <PersonIcon sx={{ mr: 1, color: 'primary.main' }} />
                  <Typography variant="subtitle2">{clockIn.first_name} {clockIn.last_name}</Typography>
                </Box>
                <Typography variant="body2" color="text.secondary">{clockIn.company_name || 'No company'}</Typography>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 1 }}>
                  <Typography variant="caption" color="text.secondary">
                    Since {formatTimeInTimezone(clockIn.clock_in_time, getUserTimezone())}
                  </Typography>
                  <Chip size="small" label={`${Math.floor(elapsed / 3600)}h ${Math.floor((elapsed % 3600) / 60)}m`} color="success" variant="outlined" />
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                  <Button size="small" color="warning" variant="outlined" onClick={() => setCancelTarget(clockIn)}>
                    Cancel Clock-In
                  </Button>
                  <Tooltip title="Removes this open clock-in WITHOUT creating a time entry. Use it after you have added the shift as a manual entry (for example when an offline Clock Out could not be recorded), or when someone clocked in by mistake.">
                    <InfoIcon fontSize="small" color="action" sx={{ ml: 0.5 }} />
                  </Tooltip>
                </Box>
              </Card>
            </Grid>
          );
        })}
      </Grid>
      <Dialog open={!!cancelTarget} onClose={() => !cancelBusy && setCancelTarget(null)}>
        <DialogTitle>Cancel Clock-In</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Remove the open clock-in for {cancelTarget?.first_name} {cancelTarget?.last_name}? No time entry is created, so add the shift as a manual entry first if the hours were worked.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCancelTarget(null)} disabled={cancelBusy}>Keep</Button>
          <Button color="warning" variant="contained" onClick={confirmCancel} disabled={cancelBusy}>Cancel Clock-In</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default function AdminHome() {
  const { user, isAdmin, isProgramAdmin, isPayrollAdmin, hasPermission } = useAuth();
  const { approvalEnabled } = usePipeline();
  const { features, loaded: featuresLoaded } = useFeatures();
  const [inbox, setInbox] = useState(null);
  const [pendingCounts, setPendingCounts] = useState({ approvals: 0, pto: 0 });
  const [recentInvoices, setRecentInvoices] = useState([]);
  const [activity, setActivity] = useState([]);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);

  const adminFamily = isAdmin || isProgramAdmin || isPayrollAdmin;
  // Same checks as the server routes (utils/endpointAccess.js).
  const canInvoices = canListInvoices(user);
  const canInbox = canReadAdminInbox(user);
  const canApproveTime = adminFamily || hasPermission('can_approve_time');
  const canApprovePTO = adminFamily || hasPermission('can_approve_pto');
  // Same gate as GET /api/clockin/active (admin-like roles, manager role, or can_approve_time)
  const canSeeActive = adminFamily || user?.role === 'manager' || hasPermission('can_approve_time');

  const period = getPayPeriodWindows();

  useEffect(() => {
    const load = async () => {
      try {
        // Activity feed shows the latest few entries; cap the fetch window
        const activityWindowStart = new Date(Date.now() - 90 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];
        const [inboxRes, invoicesRes, entriesRes, approvalsRes] = await Promise.all([
          canInbox ? getAdminInbox(getPayPeriodWindows().params) : Promise.resolve({ data: {} }),
          canInvoices ? getInvoices().catch(() => ({ data: [] })) : Promise.resolve({ data: [] }),
          getTimeEntries({ start_date: activityWindowStart }).catch(() => ({ data: [] })),
          approvalEnabled && canApproveTime
            ? getPendingApprovalCount().catch(() => ({ data: { count: 0 } }))
            : Promise.resolve({ data: { count: 0 } }),
        ]);
        setInbox(inboxRes.data);
        setPendingCounts((c) => ({ ...c, approvals: approvalsRes.data.count || 0 }));

        const invoices = (invoicesRes.data || [])
          .slice()
          .sort((a, b) => (b.invoice_date || '').localeCompare(a.invoice_date || ''))
          .slice(0, 6);
        setRecentInvoices(invoices);

        const entries = (Array.isArray(entriesRes.data) ? entriesRes.data : entriesRes.data?.entries || [])
          .slice()
          .sort((a, b) => (b.entry_date || '').localeCompare(a.entry_date || ''))
          .slice(0, 5)
          .map((e) => {
            const name = [e.first_name, e.last_name].filter(Boolean).join(' ') || 'Time entry';
            return {
              key: `entry-${e.id}`,
              icon: <ClockIcon fontSize="small" />,
              primary: `${name}${e.company_name ? ` · ${e.company_name}` : ''}`,
              secondary: `${fmtDate(e.entry_date)} · ${Number(e.hours_worked || 0).toFixed(1)} hr${Number(e.hours_worked) === 1 ? '' : 's'}`,
              amount: formatPay(e.total_pay),
            };
          });
        setActivity(entries);
      } catch (err) {
        console.error('Failed to load admin dashboard:', err);
        setError(err.response?.data?.error || 'Failed to load dashboard data');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Pending PTO count: only once the tenant's real flags are loaded, and only
  // while Time off is on (its endpoints answer 403 while it is off).
  useEffect(() => {
    if (!featuresLoaded) return undefined;
    if (!(canApprovePTO && features.time_off)) {
      setPendingCounts((c) => ({ ...c, pto: 0 }));
      return undefined;
    }
    let cancelled = false;
    getPendingPTOCount()
      .then((res) => { if (!cancelled) setPendingCounts((c) => ({ ...c, pto: res.data?.count || 0 })); })
      .catch(() => { /* count stays 0 */ });
    return () => { cancelled = true; };
  }, [featuresLoaded, features.time_off, canApprovePTO]);

  const hour = new Date().getHours();
  const greeting = hour < 12 ? 'Good morning' : hour < 17 ? 'Good afternoon' : 'Good evening';
  const firstName = user?.firstName || user?.username || '';

  const payrollRun = inbox?.payrollRuns?.[0];
  const tasks = [];
  if (inbox) {
    if (pendingCounts.approvals > 0) {
      tasks.push({
        tone: 'warn', icon: <ApprovalIcon />, kicker: 'Approve time',
        count: pendingCounts.approvals, countUnit: pendingCounts.approvals === 1 ? 'entry pending' : 'entries pending',
        detail: <>Time entries from your team waiting for approval.</>,
        actionLabel: 'Review approvals', tab: 'Approvals',
      });
    }
    if (pendingCounts.pto > 0) {
      tasks.push({
        tone: 'info', icon: <TimeOffIcon />, kicker: 'Time-off requests',
        count: pendingCounts.pto, countUnit: pendingCounts.pto === 1 ? 'request pending' : 'requests pending',
        detail: <>PTO requests waiting for a decision.</>,
        actionLabel: 'Review requests', tab: 'Time Off',
      });
    }
    if (inbox.pendingExpenses && inbox.pendingExpenses.count > 0) {
      tasks.push({
        tone: 'warn', icon: <ExpenseIcon />, kicker: 'Expenses to review',
        count: inbox.pendingExpenses.count, countUnit: inbox.pendingExpenses.count === 1 ? 'expense pending' : 'expenses pending',
        detail: <><b>{formatCurrency(inbox.pendingExpenses.total)}</b> submitted and awaiting review.</>,
        actionLabel: 'Review expenses', tab: 'Expenses',
      });
    }
    if (inbox.payrollRuns && inbox.payrollRuns.length > 0) {
      tasks.push({
        tone: 'warn', icon: <PayrollIcon />, kicker: 'Approve payroll',
        count: inbox.payrollRuns.length, countUnit: inbox.payrollRuns.length === 1 ? 'run in review' : 'runs in review',
        detail: (
          <>
            <b>{fmtDate(payrollRun.start_date)} – {fmtDate(payrollRun.end_date)}</b>
            {' · '}{formatCurrency(payrollRun.total_gross)} gross → {formatCurrency(payrollRun.total_net)} net.
            {payrollRun.check_date && <> Check date <b>{fmtDate(payrollRun.check_date)}</b>.</>}
          </>
        ),
        actionLabel: 'Review & approve', tab: 'Payroll',
      });
    }
    if (inbox.draftInvoices && inbox.draftInvoices.count > 0) {
      tasks.push({
        tone: 'info', icon: <SendIcon />, kicker: 'Send invoices',
        count: inbox.draftInvoices.count, countUnit: inbox.draftInvoices.count === 1 ? 'draft ready' : 'drafts ready',
        detail: <><b>{formatCurrency(inbox.draftInvoices.total)}</b> in drafts waiting to go out.</>,
        actionLabel: 'Review drafts', tab: 'Invoices',
      });
    }
    if (inbox.ar && inbox.ar.count > 0) {
      tasks.push({
        tone: 'crit', icon: <AlertIcon />, kicker: 'Follow up on AR',
        count: inbox.ar.count, countUnit: 'sent, unpaid',
        detail: <><b>{formatCurrency(inbox.ar.total)}</b> outstanding. Oldest is <b>{inbox.ar.oldest_days} days</b> out.</>,
        actionLabel: 'View invoices', tab: 'Invoices',
      });
    }
    if (inbox.unbilled && Number(inbox.unbilled.hours) > 0) {
      tasks.push({
        tone: 'good', icon: <ClockIcon />, kicker: 'Unbilled hours',
        count: Number(inbox.unbilled.hours).toFixed(1), countUnit: 'hrs uninvoiced',
        detail: <><b>{formatCurrency(inbox.unbilled.amount)}</b> billable and not yet on an invoice.</>,
        actionLabel: 'Review timesheets', tab: 'Timesheets',
      });
    }
  }

  // Metric strip: this pay period vs the previous one
  const p = inbox?.period;
  const prev = inbox?.prevPeriod;
  const pctDelta = (cur, before) => {
    if (!before || Number(before) === 0) return null;
    const pct = ((Number(cur) - Number(before)) / Number(before)) * 100;
    return { pct: Math.round(Math.abs(pct)), up: pct >= 0 };
  };
  const hoursDelta = p && prev ? pctDelta(p.hours, prev.hours) : null;
  const costDelta = p && prev && p.payroll_cost != null && prev.payroll_cost != null ? pctDelta(p.payroll_cost, prev.payroll_cost) : null;
  const avgRate = p && p.billable_value != null && Number(p.hours) > 0 ? Number(p.billable_value) / Number(p.hours) : null;
  const margin = p && p.billable_value != null && p.payroll_cost != null && Number(p.billable_value) > 0
    ? ((Number(p.billable_value) - Number(p.payroll_cost)) / Number(p.billable_value)) * 100
    : null;

  if (loading) {
    return (
      <Box>
        <Skeleton variant="text" width={280} height={40} />
        <Grid container spacing={2} sx={{ mt: 0.5 }}>
          {[0, 1, 2, 3].map(i => (
            <Grid item xs={12} sm={6} md={3} key={i}>
              <Skeleton variant="rounded" height={140} />
            </Grid>
          ))}
        </Grid>
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.75 }}>
      {error && <Alert severity="error" onClose={() => setError('')}>{error}</Alert>}

      {/* Page head */}
      <Box sx={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap' }}>
        <Box>
          <Typography variant="h1" component="h1">{greeting}{firstName ? `, ${firstName}` : ''}</Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.5 }}>
            {tasks.length === 0 ? 'Nothing needs your attention right now.' : `${tasks.length} ${tasks.length === 1 ? 'thing needs' : 'things need'} you today.`}
          </Typography>
        </Box>
        <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 1, bgcolor: 'background.paper', border: 1, borderColor: 'divider', borderRadius: '999px', px: 1.75, py: 0.75, fontSize: '0.8125rem' }}>
          <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: 'primary.main' }} />
          <Typography component="span" sx={{ fontSize: 'inherit', fontWeight: 600 }}>Pay period</Typography>
          <Typography component="span" sx={{ fontSize: 'inherit', color: 'text.faint' }}>
            {fmtDate(period.params.start_date)} – {fmtDate(period.params.end_date)} · day {period.dayInPeriod} of {period.daysInPeriod}
          </Typography>
        </Box>
      </Box>

      {/* Personal clock — managers and admins log time too */}
      <ClockCard />

      {canSeeActive && <CurrentlyClockedIn />}

      {/* Action inbox */}
      <Box>
        <Typography variant="overline" sx={{ color: 'text.faint', letterSpacing: '0.08em' }}>Needs your attention</Typography>
        {tasks.length === 0 ? (
          <Card sx={{ mt: 1, p: 2.5, display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <AllClearIcon sx={{ color: 'success.main' }} />
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              All caught up — nothing is waiting on you right now.
            </Typography>
          </Card>
        ) : (
          <Grid container spacing={1.75} sx={{ mt: -0.5 }}>
            {tasks.map((t) => (
              <Grid item xs={12} sm={6} lg={3} key={t.kicker}>
                <TaskCard {...t} onAction={() => navigateToTab(t.tab)} />
              </Grid>
            ))}
          </Grid>
        )}
      </Box>

      {/* Metric strip */}
      {p && (
        <Box>
          <Typography variant="overline" sx={{ color: 'text.faint', letterSpacing: '0.08em' }}>
            This pay period{inbox?.teamScoped ? ' · your team' : ''}
          </Typography>
          <Grid container spacing={1.75} sx={{ mt: -0.5 }}>
            <Grid item xs={12} sm={6} md={3}>
              <MetricTile
                label="Hours logged"
                value={Number(p.hours).toFixed(1)}
                delta={hoursDelta ? { text: `${hoursDelta.up ? '▲' : '▼'} ${hoursDelta.pct}%`, direction: hoursDelta.up ? 'up' : 'down' } : null}
                foot={hoursDelta ? 'vs last period' : null}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <MetricTile
                label="Billable value"
                value={formatPay(p.billable_value)}
                foot={avgRate != null ? `avg ${formatCurrency(avgRate)} / hr` : null}
                info={p.billable_value == null ? 'Hidden — needs the Manage invoices permission' : 'Hours × client bill rate for entries in this pay period'}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <MetricTile
                label="Payroll cost"
                value={formatPay(p.payroll_cost)}
                delta={costDelta ? { text: `${costDelta.up ? '▲' : '▼'} ${costDelta.pct}%`, direction: costDelta.up ? 'down' : 'up' } : null}
                foot={costDelta ? 'vs last period' : null}
                info={p.payroll_cost == null ? 'Hidden — needs the View pay rates permission' : 'Hours × employee pay rate for entries in this pay period'}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <MetricTile
                label="Gross margin"
                value={margin != null ? margin.toFixed(1) : '—'}
                valueSuffix={margin != null ? '%' : null}
                info="(Billable value − payroll cost) ÷ billable value"
              />
            </Grid>
          </Grid>
        </Box>
      )}

      {/* Recent invoices + activity */}
      <Grid container spacing={2.75}>
        {canInvoices && (
        <Grid item xs={12} lg={7}>
          <Card>
            <Box sx={{ display: 'flex', alignItems: 'center', px: 2, py: 1.5, borderBottom: 1, borderColor: 'divider' }}>
              <Typography variant="h2" component="h2">Recent invoices</Typography>
              <Box sx={{ flexGrow: 1 }} />
              <Link component="button" underline="none" onClick={() => navigateToTab('Invoices')} sx={{ fontSize: '0.8125rem', fontWeight: 600 }}>
                View all →
              </Link>
            </Box>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Invoice</TableCell>
                    <TableCell>Client</TableCell>
                    <TableCell>Date</TableCell>
                    <TableCell align="right">Amount</TableCell>
                    <TableCell>Status</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {recentInvoices.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={5} align="center" sx={{ py: 3, color: 'text.secondary' }}>No invoices yet</TableCell>
                    </TableRow>
                  ) : recentInvoices.map((inv) => {
                    const overdue = inv.status === 'sent' && inv.due_date && inv.due_date.split('T')[0] < new Date().toISOString().split('T')[0];
                    return (
                      <TableRow key={inv.id} hover>
                        <TableCell sx={{ fontWeight: 600 }}>{inv.invoice_number}</TableCell>
                        <TableCell>{inv.company_name}</TableCell>
                        <TableCell sx={{ color: 'text.secondary' }}>{fmtDate(inv.invoice_date)}</TableCell>
                        <TableCell align="right">{formatCurrency(inv.total_amount)}</TableCell>
                        <TableCell><StatusChip status={overdue ? 'overdue' : inv.status} /></TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </Card>
        </Grid>
        )}
        <Grid item xs={12} lg={canInvoices ? 5 : 12}>
          <Card sx={{ height: '100%' }}>
            <Box sx={{ px: 2, py: 1.5, borderBottom: 1, borderColor: 'divider' }}>
              <Typography variant="h2" component="h2">Recent activity</Typography>
            </Box>
            {activity.length === 0 ? (
              <Typography variant="body2" sx={{ color: 'text.secondary', p: 2.5, textAlign: 'center' }}>No recent activity</Typography>
            ) : (
              <List disablePadding>
                {activity.map((a, i) => (
                  <React.Fragment key={a.key}>
                    {i > 0 && <Divider component="li" />}
                    <ListItem sx={{ px: 2, py: 1.25 }}>
                      <ListItemIcon sx={{ minWidth: 42 }}>
                        <Box sx={{ width: 30, height: 30, borderRadius: '8px', bgcolor: 'background.surface2', color: 'text.secondary', display: 'grid', placeItems: 'center', '& svg': { fontSize: 15 } }}>
                          {a.icon}
                        </Box>
                      </ListItemIcon>
                      <ListItemText
                        primary={a.primary}
                        secondary={a.secondary}
                        primaryTypographyProps={{ fontSize: '0.8125rem' }}
                        secondaryTypographyProps={{ fontSize: '0.6875rem' }}
                      />
                      <Typography sx={{ fontSize: '0.8125rem', fontWeight: 600, ml: 1 }}>{a.amount}</Typography>
                    </ListItem>
                  </React.Fragment>
                ))}
              </List>
            )}
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
}
